/**
 * Combined Role and Role Level Distribution Chart
 * Shows both distributions in a single donut/sunburst chart
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DonutSunburstChart } from './DonutSunburstChart';
import { StarburstChart } from './CompanyCharts';
import { hasAnyValidNumber } from '../utils/numberUtils';

interface RoleRoleLevelChartProps {
  roleDistribution?: Record<string, number>;
  roleLevelDistribution?: Record<string, number>;
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
}

export const RoleRoleLevelChart: React.FC<RoleRoleLevelChartProps> = memo(({
  roleDistribution,
  roleLevelDistribution,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
}) => {
  const intl = useIntl();
  
  const hasRoleData = roleDistribution &&
    Object.keys(roleDistribution).length > 0 &&
    Object.values(roleDistribution).some(val => hasAnyValidNumber(val));
  
  const hasRoleLevelData = roleLevelDistribution &&
    Object.keys(roleLevelDistribution).length > 0 &&
    Object.values(roleLevelDistribution).some(val => hasAnyValidNumber(val));
  
  // Don't render if neither has data
  if (!hasRoleData && !hasRoleLevelData) {
    return null;
  }
  
  // Determine which dimension has more values (goes on outside donut)
  // The dimension with MORE values goes on the outside, fewer values on the inside
  const roleCount = hasRoleData ? Object.keys(roleDistribution!).length : 0;
  const roleLevelCount = hasRoleLevelData ? Object.keys(roleLevelDistribution!).length : 0;
  
  const useCombinedChart = hasRoleData && hasRoleLevelData && roleCount > 0 && roleLevelCount > 0;
  // Invert: roleLevelOnOutside means roleLevel has MORE values (goes outside)
  // But we want roles (which typically have more values) on the outside
  const roleOnOutside = roleCount >= roleLevelCount;
  
  // Generate separate captions for outer and inner
  const generateOuterCaption = () => {
    if (!companyName) return null;
    
    if (roleOnOutside && hasRoleData && roleDistribution) {
      const roleEntries = Object.entries(roleDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (roleEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionSingle"
              defaultMessage="{companyName} hires mostly {role} roles."
              values={{ companyName, role: roleEntries[0][0] }}
            />
          </p>
        );
      } else if (roleEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionDouble"
              defaultMessage="{companyName} hires mostly {role1} and {role2} roles."
              values={{ companyName, role1: roleEntries[0][0], role2: roleEntries[1][0] }}
            />
          </p>
        );
      } else if (roleEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionTriple"
              defaultMessage="{companyName} hires mostly {role1}, {role2}, and {role3} roles."
              values={{ companyName, role1: roleEntries[0][0], role2: roleEntries[1][0], role3: roleEntries[2][0] }}
            />
          </p>
        );
      }
    } else if (!roleOnOutside && hasRoleLevelData && roleLevelDistribution) {
      const roleLevelEntries = Object.entries(roleLevelDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (roleLevelEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionSingle"
              defaultMessage="{companyName} hires mostly at the {level} level."
              values={{ companyName, level: roleLevelEntries[0][0] }}
            />
          </p>
        );
      } else if (roleLevelEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionDouble"
              defaultMessage="{companyName} hires mostly at the {level1} and {level2} levels."
              values={{ companyName, level1: roleLevelEntries[0][0], level2: roleLevelEntries[1][0] }}
            />
          </p>
        );
      } else if (roleLevelEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionTriple"
              defaultMessage="{companyName} hires mostly at the {level1}, {level2}, and {level3} levels."
              values={{ companyName, level1: roleLevelEntries[0][0], level2: roleLevelEntries[1][0], level3: roleLevelEntries[2][0] }}
            />
          </p>
        );
      }
    }
    return null;
  };
  
  const generateInnerCaption = () => {
    if (!companyName) return null;
    
    if (roleOnOutside && hasRoleLevelData && roleLevelDistribution) {
      const roleLevelEntries = Object.entries(roleLevelDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (roleLevelEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionSingle"
              defaultMessage="{companyName} hires mostly at the {level} level."
              values={{ companyName, level: roleLevelEntries[0][0] }}
            />
          </p>
        );
      } else if (roleLevelEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionDouble"
              defaultMessage="{companyName} hires mostly at the {level1} and {level2} levels."
              values={{ companyName, level1: roleLevelEntries[0][0], level2: roleLevelEntries[1][0] }}
            />
          </p>
        );
      } else if (roleLevelEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-level-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Level Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.levelDistributionTriple"
              defaultMessage="{companyName} hires mostly at the {level1}, {level2}, and {level3} levels."
              values={{ companyName, level1: roleLevelEntries[0][0], level2: roleLevelEntries[1][0], level3: roleLevelEntries[2][0] }}
            />
          </p>
        );
      }
    } else if (!roleOnOutside && hasRoleData && roleDistribution) {
      const roleEntries = Object.entries(roleDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (roleEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionSingle"
              defaultMessage="{companyName} hires mostly {role} roles."
              values={{ companyName, role: roleEntries[0][0] }}
            />
          </p>
        );
      } else if (roleEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionDouble"
              defaultMessage="{companyName} hires mostly {role1} and {role2} roles."
              values={{ companyName, role1: roleEntries[0][0], role2: roleEntries[1][0] }}
            />
          </p>
        );
      } else if (roleEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#role-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Role Distribution:</a>{' '}
            <FormattedMessage
              id="companyChart.caption.roleDistributionTriple"
              defaultMessage="{companyName} hires mostly {role1}, {role2}, and {role3} roles."
              values={{ companyName, role1: roleEntries[0][0], role2: roleEntries[1][0], role3: roleEntries[2][0] }}
            />
          </p>
        );
      }
    }
    return null;
  };
  
  const outerCaption = generateOuterCaption();
  const innerCaption = generateInnerCaption();
  
  return (
    <div className={`role-rolelevel-chart ${className} flex flex-col items-center`}>
      <h3 className="text-base font-semibold text-jc-ink mb-2 w-full">
        <FormattedMessage
          id="jobDetailPage.roleAndRoleLevelDistribution"
          defaultMessage="Role & Role Level Distribution"
          description="Chart title for combined role and role level distribution visualization"
        />
      </h3>
      
      {useCombinedChart ? (
        // Combined donut/sunburst chart
        // More values go on outside (donut), fewer values go on inside (sunburst)
        <div className="w-full">
          <DonutSunburstChart
            outerData={roleOnOutside ? roleDistribution! : roleLevelDistribution!}
            innerData={roleOnOutside ? roleLevelDistribution! : roleDistribution!}
            outerTitle={roleOnOutside 
              ? intl.formatMessage({ id: "jobDetailPage.roleDistribution", defaultMessage: "Role Distribution", description: "Chart title for role distribution showing job role types" })
              : intl.formatMessage({ id: "jobDetailPage.roleLevelDistribution", defaultMessage: "Role Level Distribution", description: "Chart title for role level distribution showing experience levels" })}
            innerTitle={roleOnOutside
              ? intl.formatMessage({ id: "jobDetailPage.roleLevelDistribution", defaultMessage: "Role Level Distribution", description: "Chart title for role level distribution showing experience levels" })
              : intl.formatMessage({ id: "jobDetailPage.roleDistribution", defaultMessage: "Role Distribution", description: "Chart title for role distribution showing job role types" })}
            brandColour={brandColour}
            companyName={companyName}
            locale={locale}
            maxInnerItems={12}
            minOuterPercentage={roleOnOutside ? 3 : undefined}
            outerCaption={outerCaption}
            innerCaption={innerCaption}
          />
        </div>
      ) : (
        // Fallback to side-by-side charts if we don't have both
        <div className="flex flex-col md:flex-row gap-6 items-start justify-center w-full">
          {hasRoleData && (
            <div className="flex-1 min-w-0">
              <StarburstChart
                data={roleDistribution!}
                title={intl.formatMessage({ id: "jobDetailPage.roleDistribution", defaultMessage: "Role Distribution", description: "Chart title for role distribution showing job role types" })}
                titleKey="roleDistribution"
                brandColour={brandColour}
                companyName={companyName}
                locale={locale}
                maxItems={8}
              />
            </div>
          )}
          {hasRoleLevelData && (
            <div className="flex-1 min-w-0">
              <StarburstChart
                data={roleLevelDistribution!}
                title={intl.formatMessage({ id: "jobDetailPage.roleLevelDistribution", defaultMessage: "Role Level Distribution", description: "Chart title for role level distribution showing experience levels" })}
                titleKey="roleLevelDistribution"
                brandColour={brandColour}
                companyName={companyName}
                locale={locale}
                maxItems={8}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

RoleRoleLevelChart.displayName = 'RoleRoleLevelChart';

